import { useState } from 'react';
import { useDmDoctorSearch } from '../../../../../hooks/usePracticeDm';
import type { DmDoctorOption } from '../../../../../services/practiceDm';
import { DmDoctorOptionRow } from './DmOptionRows';
import { DmModalBody } from './DmModalBody';
import { DmModalFooter } from './DmModalFooter';
import { DmSearchList } from './DmSearchList';
import { DoctorPracticePicker } from './DoctorPracticePicker';
import type { DmLinkSave } from './useDmLinkSave';

interface ExistingDoctorPaneProps {
    practiceId: string;
    initialSearch: string;
    save: DmLinkSave;
    isSaving: boolean;
    onCancel: () => void;
}

function defaultPracticeId(doctor: DmDoctorOption): number | null {
    const hasMain = doctor.practices.some((p) => p.practiceId === doctor.mainPracticeId);
    return hasMain ? doctor.mainPracticeId : (doctor.practices[0]?.practiceId ?? null);
}

export function ExistingDoctorPane({
    practiceId,
    initialSearch,
    save,
    isSaving,
    onCancel
}: ExistingDoctorPaneProps) {
    const [search, setSearch] = useState(initialSearch);
    const [doctor, setDoctor] = useState<DmDoctorOption | null>(null);
    const [dmPracticeId, setDmPracticeId] = useState<number | null>(null);
    const searchState = useDmDoctorSearch(practiceId, search);

    const pickDoctor = (next: DmDoctorOption) => {
        setDoctor(next);
        setDmPracticeId(defaultPracticeId(next));
    };
    const confirm = () => {
        if (doctor && dmPracticeId !== null) void save(doctor.doctorId, dmPracticeId);
    };

    return (
        <>
            <DmModalBody>
                <DmSearchList
                    search={search}
                    onSearchChange={setSearch}
                    state={searchState}
                    placeholder="Search DM doctors by name, email or id"
                    emptyLabel="No doctors found"
                    renderItem={(option) => (
                        <DmDoctorOptionRow
                            key={option.doctorId}
                            doctor={option}
                            selected={option.doctorId === doctor?.doctorId}
                            onSelect={() => pickDoctor(option)}
                        />
                    )}
                />
                {doctor && (
                    <DoctorPracticePicker
                        doctor={doctor}
                        value={dmPracticeId}
                        onChange={setDmPracticeId}
                    />
                )}
            </DmModalBody>
            <DmModalFooter
                onCancel={onCancel}
                primaryLabel="Confirm"
                pendingLabel="Saving…"
                onPrimary={confirm}
                isPending={isSaving}
                primaryDisabled={!doctor || dmPracticeId === null}
            />
        </>
    );
}
